/**
 * Feature #534 — pure, Angular-free helpers for the "Kombinationen einzeln" toggle.
 *
 * Interchanged compositions (articles with swapped components, e.g. a Gin Tonic poured with a different gin)
 * are counted as ONE article by default ({@link CompositionGroupingMode} `'combined'`). This toggle lets the
 * user split them back out into one row per composition + swap combination (`'individual'`).
 *
 * There is deliberately NO backend change for this: `CompositionOverrideLinkerEdto.CompositeId`
 * (`API.Core/Models/Inventory/CompositionOverrideLinker.cs`, built by
 * `Shared.Core/Extensions/InterchangedComponentExtensions.cs#GetCompositeId`) is already on the wire, shaped
 * `compositionId|overrideId|sortedSwapIds|additional` — `|`-joined, with the swap segment itself `_`-joined and
 * ordinal-sorted (order independent, see `GetInterchangedCompositeId`/`GetInterchangedLinkerCompositeId`). The
 * variant key is therefore derived here, on the client, from segments already present on every line.
 */

/**
 * `'combined'` (default): one row per article, every swap combination merged into it — today's behaviour.
 * `'individual'`: one row per article PLUS swap combination (the Sonderpreis/override segment is still merged,
 * see {@link compositionVariantKey}).
 */
export type CompositionGroupingMode = 'combined' | 'individual';

/**
 * The single `localStorage` key both the event-details page and the single-event-analysis page read/write
 * through, so switching the toggle on one page is reflected on the other (decision: one shared preference, not
 * a per-page one).
 */
export const COMPOSITION_GROUPING_STORAGE_KEY = 'composition_grouping_mode';

/**
 * Derives the client-side "variant key" for one composition line from its server-provided composite id.
 *
 * The server's composite id is `compositionId|overrideId|sortedSwapIds|additional` (see the module doc above).
 * We deliberately keep only segment 0 (the composition id) and segment 2 (the sorted swap ids) — the override
 * segment (index 1, the Sonderpreis/price-override id) is DROPPED on purpose: per the #534 decision, a
 * price-override variant must still merge with the list-price variant of the same swap combination, it must
 * not become its own row. Order independence for the swap segment itself comes for free from the server's own
 * ordinal sort, so no re-sorting is needed here.
 *
 * Degrades to `compositionId` (i.e. behaves like `'combined'` mode) whenever the composite id cannot be trusted:
 * - `compositeId` is `null`/`undefined`/blank — e.g. a trimmed `?fields=` projection that never named
 *   `CompositeId` (see the #440-style guard documented on `CompositionOverrideLinkerTransfer.modificationLines`
 *   in `src/app/_models/_booking/composition-override-linker-transfer.ts`): this must never throw.
 * - `compositeId` has fewer than 3 `|`-separated segments (malformed/truncated data).
 * - segment 0 itself is blank (falls back to the caller-supplied `compositionId`, which is always known).
 */
export function compositionVariantKey(compositeId: string | null | undefined, compositionId: string): string {
  if (compositeId == null || compositeId.trim() === '') {
    return compositionId;
  }

  const segments = compositeId.split('|');
  if (segments.length < 3) {
    return compositionId;
  }

  const segment0 = segments[0].trim() !== '' ? segments[0] : compositionId;
  const segment2 = segments[2];
  return `${segment0}|${segment2}`;
}

/**
 * The grouping key one composition line should be bucketed under for a given {@link CompositionGroupingMode}:
 * the plain `compositionId` in `'combined'` mode, or {@link compositionVariantKey} in `'individual'` mode.
 * Centralised so every consumer (event-details hero tiles/cards, the analysis table, top sales) buckets rows
 * identically instead of re-implementing the branch.
 */
export function compositionGroupKey(
  line: {compositeId?: string | null; compositionId: string},
  mode: CompositionGroupingMode
): string {
  return mode === 'individual' ? compositionVariantKey(line.compositeId, line.compositionId) : line.compositionId;
}

/**
 * Builds the display label for one composition variant row in `'individual'` mode: the article name, followed
 * by one line per modification, formatted `${connector} ${text}` (matching the #197/#198 box-drawing
 * connector/text pairs already rendered elsewhere — `ModificationLineTransfer`). A blank connector renders as
 * just the text (no leading space). With no modification lines at all, the label is just the name — so this
 * function is always safe to call in `'combined'` mode too, where it degenerates to the plain article name.
 */
export function compositionVariantLabel(
  name: string,
  modificationLines: ReadonlyArray<{connector: string; text: string}> | null | undefined
): string {
  if (!modificationLines || modificationLines.length === 0) {
    return name;
  }

  const lines = modificationLines.map(line => line.connector ? `${line.connector} ${line.text}` : line.text);
  return [name, ...lines].join('\n');
}

/**
 * The wire form of a {@link CompositionGroupingMode} (#716 / Task #722) — the value that travels on the event
 * reads' `grouping` parameter and comes back stamped on `EventOrderStats.grouping`.
 *
 * Deliberately the .NET enum's NAME rather than its ordinal: the query string stays readable, ASP.NET binds it
 * case-insensitively, and adding a grain later cannot renumber an existing one.
 */
export function compositionGroupingParameter(mode: CompositionGroupingMode): 'Combined' | 'Individual' {
  return mode === 'individual' ? 'Individual' : 'Combined';
}

/**
 * Parses a persisted/raw value (e.g. straight out of `localStorage`) into a {@link CompositionGroupingMode}.
 * Only the exact string `'individual'` yields `'individual'` — anything else (missing key, `null`, garbage,
 * an old/foreign value) safely degrades to the default `'combined'`, so a corrupted or unrelated stored value
 * can never put the UI in an unexpected state.
 */
export function parseCompositionGroupingMode(raw: string | null | undefined): CompositionGroupingMode {
  return raw === 'individual' ? 'individual' : 'combined';
}
